import { type CSSProperties, type ReactNode, useRef, useState, useLayoutEffect } from 'react';
import { Box } from '@mantine/core';
import { motion, useMotionValue, useDragControls, animate, type PanInfo } from 'framer-motion';

const CLOSE_THRESHOLD = 100;
const CLOSE_VELOCITY = 500;

/** Convert a maxHeight CSS value to pixels, assuming parent is viewport-sized. */
const toPixels = (value: string | number): number => {
  if (typeof value === 'number') return value;
  if (value.endsWith('vh')) return (parseFloat(value) / 100) * window.innerHeight;
  if (value.endsWith('%')) return (parseFloat(value) / 100) * window.innerHeight;
  if (value.endsWith('px')) return parseFloat(value);
  return window.innerHeight * 0.92;
};

type SwipeToCloseDrawerContentProps = {
  onClose: () => void;
  header: ReactNode;
  children: ReactNode;
  /** Extra styles applied to the outer container (e.g. fontFamily) */
  contentStyle?: CSSProperties;
  /** Maximum height of the drawer (default: '92vh'). Content shorter than this will shrink the drawer. */
  maxHeight?: string | number;
  /** Whether swipe-to-close is allowed (default: true). When false, the drag handle is hidden and swipe gestures are disabled. */
  allowSwipeClose?: boolean;
  /**
   * Shrink the sheet to fit short content (default: true). When false, the
   * sheet is pinned at maxHeight and never re-measures, so content or
   * viewport changes while open cannot move it.
   */
  shrinkToContent?: boolean;
};

export const SwipeToCloseDrawerContent = ({ onClose, header, children, contentStyle, maxHeight = '92vh', allowSwipeClose = true, shrinkToContent = true }: SwipeToCloseDrawerContentProps) => {
  const y = useMotionValue(0);
  const dragControls = useDragControls();
  const headerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const maxPx = toPixels(maxHeight);
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(maxPx);

  useLayoutEffect(() => {
    if (!shrinkToContent) {
      setHeight(maxPx);
      return;
    }

    const headerEl = headerRef.current;
    const innerEl = innerRef.current;
    if (!headerEl || !innerEl) return;

    const measure = () => {
      const headerH = headerEl.offsetHeight;
      const contentH = innerEl.offsetHeight;
      setHeight(Math.min(headerH + contentH, maxPx));
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(innerEl);
    return () => ro.disconnect();
  }, [maxPx, shrinkToContent]);

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > CLOSE_THRESHOLD || info.velocity.y > CLOSE_VELOCITY) {
      animate(y, window.innerHeight, {
        type: 'tween',
        duration: 0.2,
        onComplete: onClose,
      });
    } else {
      animate(y, 0, { type: 'spring', stiffness: 500, damping: 30 });
    }
  };

  return (
    <motion.div
      style={{
        y,
        marginTop: 'auto',
        height,
        transition: 'height 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--mantine-color-body)',
        borderTopLeftRadius: 'var(--mantine-radius-lg)',
        borderTopRightRadius: 'var(--mantine-radius-lg)',
        pointerEvents: 'auto',
        ...contentStyle,
      }}
      drag={allowSwipeClose ? "y" : false}
      dragControls={dragControls}
      dragListener={false}
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={{ top: 0, bottom: 0.6 }}
      onDragEnd={handleDragEnd}
    >
      {/* Drag handle area — only this initiates the drag */}
      <Box
        ref={headerRef}
        onPointerDown={allowSwipeClose ? (e) => dragControls.start(e) : undefined}
        style={{
          touchAction: allowSwipeClose ? 'none' : undefined,
          cursor: allowSwipeClose ? 'grab' : undefined,
        }}
      >
        {allowSwipeClose && (
          <Box
            py="sm"
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                backgroundColor: 'var(--mantine-color-gray-3)',
              }}
            />
          </Box>
        )}
        <Box px="md" pb="md" pt={allowSwipeClose ? undefined : "md"}>
          {header}
        </Box>
      </Box>

      {/* Scrollable content */}
      {/* overscrollBehavior none: suppress the rubber-band bounce, which drags
          content past the sheet's rounded top corners */}
      <Box ref={scrollRef} style={{ flex: 1, overflow: 'auto', minHeight: 0, overscrollBehavior: 'none' }} px="md">
        <div ref={innerRef} style={{ display: 'flow-root', paddingBottom: 'var(--mantine-spacing-xl)' }}>
          {children}
        </div>
      </Box>
    </motion.div>
  );
};
